"use client"

import { useEffect, useState } from "react"

import { cn, SofiaMark } from "@coval/ui"

import { useDelayedUnmount } from "../hooks/useDelayedUnmount"

/**
 * Size of the mark in the transcript's avatar column. The indicator and the
 * reply row it hands off to share it so the mark doesn't jump when reply text
 * arrives.
 */
export const SOFIA_AVATAR_SIZE = 20

/**
 * The "thinking" labels Sofia shows while a turn is in flight.
 *
 * The chat endpoint is a *completion* endpoint: the frontend gets no live
 * per-step signal, only the final payload, so this indicator has NO honest way
 * to know how close the turn is to done. It therefore only ever states what is
 * actually true — that Sofia is working — and never predicts completion.
 *
 * Earlier this cycled "Researching → Considering options → Almost done", but a
 * pure timer that flips to "Almost done" at ~9s routinely lied: the turn often
 * ran 15s+ longer, so the label promised a finish that wasn't near. That's
 * removed. The stages now escalate only in how they describe *duration* (a turn
 * that runs long gets "Still working…"), never in how *complete* they claim to
 * be. When real progress signal exists (streaming — see Task C / the `running`
 * field on tool items), a genuine near-completion state can be added back; there
 * is no such signal today, so we don't fake one.
 */
const THINKING_STAGES: { label: string; afterMs: number }[] = [
  { label: "Working", afterMs: 0 },
  { label: "Analyzing", afterMs: 4000 },
  { label: "Still working", afterMs: 12000 },
]

/**
 * Sofia's thinking indicator: the animated mark shown in the pending-message
 * slot while Sofia works but before any reply text arrives. The mark sits in the
 * same avatar column as Sofia's messages and its waveform "dances" (via
 * `animated`) so her identity stays put across the think → reply transition.
 * During the 150ms handoff to the reply row, the particle field starts
 * reforming into the static logo as the indicator fades. Alongside the mark it shows a status
 * label that escalates only by elapsed time (working → analyzing → still
 * working) so the user knows Sofia is still busy WITHOUT ever claiming the turn
 * is nearly done — the completion endpoint gives no signal to back such a claim.
 * No separate spinner — the dancing mark is the loading affordance. Respects
 * prefers-reduced-motion (mark goes static).
 */
export function CoviThinkingIndicator({ visible }: { visible: boolean }) {
  const { mounted, leaving } = useDelayedUnmount(visible, 150)
  const [stage, setStage] = useState(0)

  // Advance through the phased labels while visible; reset to the first stage
  // whenever a new turn starts so each turn begins at "researching".
  useEffect(() => {
    if (!visible) {
      setStage(0)
      return
    }
    setStage(0)
    const timers = THINKING_STAGES.slice(1).map((entry, index) =>
      setTimeout(() => setStage(index + 1), entry.afterMs),
    )
    return () => timers.forEach(clearTimeout)
  }, [visible])

  if (!mounted) {
    return null
  }
  const label = THINKING_STAGES[stage]?.label ?? THINKING_STAGES[0].label
  return (
    <div
      role="status"
      aria-label="Sofia is thinking"
      title="Sofia is thinking"
      className={cn(
        "flex items-center gap-1.5 transition-opacity duration-150 ease-out",
        leaving ? "opacity-0" : "opacity-100",
      )}
    >
      <SofiaMark
        className="shrink-0"
        size={SOFIA_AVATAR_SIZE}
        animated={!leaving}
      />
      <span className="text-xs text-coval-text-muted">{label}…</span>
    </div>
  )
}
